/**
 * CohortBoardView
 *
 * Standalone matrix view for cohort boards. Can be mounted from the Cohorts
 * tab or directly via a deep link.
 *
 * Layout:
 *   - Left column : card title cells (KanbanCard-style)
 *   - Right columns: one per board status — headers match KanbanColumn style,
 *                    cells contain per-member avatar bubbles
 *
 * Card creation (admin / mentor only):
 *   - FAB "+" button (same as KanbanBoard) opens TaskEditPopup in create mode.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Avatar } from '@/components/ui';
import { useThemeColors, withOpacity, opacity } from '@/styles';
import KanbanCard from '@/components/core/Kanban/KanbanCard';
import type { BoardTask } from '@/types/models/BoardTask';
import {
  spacing,
  radius,
  typography,
  layout,
  sizing,
} from '@/styles/design-tokens';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
// eslint-disable-next-line no-restricted-imports -- direct call intentional: drag-and-drop optimistic moves + silent background fetches; toasts would be disruptive mid-drag
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import { getTheme, hasTheme, applyVariant } from '@/styles/colors/themes';
import { parseStoredTheme } from '@/styles/theme/ThemeContext';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useUserMap, getUserDisplayName } from '@/hooks/queries';
import { UserRole } from '@/types/enums/UserRole';
import { BoardTag } from '@/types/enums/BoardTag';
import { Icon, IconName } from '@/icons';
import { DefaultEmptyState } from '@/components/core/EmptyStates';
import TaskEditPopup from '@/components/core/Kanban/TaskEditPopup';
import { ConfirmOnExitDialog } from '@/components/core/Kanban/ConfirmOnExitDialog';
import { ConfirmOnEntryDialog } from '@/components/core/Kanban/ConfirmOnEntryDialog';
import { Alert, useAlert } from '@/components/core/Alert';
import { getResponseCodeMessage } from '@/business/constants/response-codes';
import { useConfirmOnExit } from '@/hooks/useConfirmOnExit';
import { useConfirmOnEntry } from '@/hooks/useConfirmOnEntry';
import { ConfirmableField } from '@/types/ConfirmableField';
import { validateCardMove } from '@/types/board-config.types';
import type { ColorPalette } from '@/styles/colors/types';
import type { BoardStatusWithCards } from '@/types/board-config.types';
import type {
  GetCohortProgressInput,
  GetCohortProgressOutput,
  GetCohortProgressStatusesItem,
  GetCohortProgressTasksItem,
  GetCohortProgressMembersItem,
  GetUserProfileInput,
  GetUserProfileOutput,
  GetTaskInput,
  GetTaskOutput,
  UpdateTaskStatusInput,
  UpdateTaskStatusOutput,
} from '@/types/generated';
import type { WeekInfo } from '@/utils/week';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CohortBoardViewProps {
  boardId: string;
  selectedWeek: WeekInfo;
  refreshTrigger?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPaletteForThemeString(stored?: string): ColorPalette | null {
  if (!stored) return null;
  const { theme, variant } = parseStoredTheme(stored);
  if (!hasTheme(theme)) return null;
  return applyVariant(getTheme(theme).palette, variant);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CohortBoardView({
  boardId,
  selectedWeek,
  refreshTrigger,
}: CohortBoardViewProps) {
  const colors = useThemeColors();
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const { userMap } = useUserMap(activeOrganizationId ?? undefined);
  const alert = useAlert();

  const [isLoading, setIsLoading] = useState(true);
  const [statuses, setStatuses] = useState<GetCohortProgressStatusesItem[]>([]);
  const [tasks, setTasks] = useState<GetCohortProgressTasksItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [userPaletteMap, setUserPaletteMap] = useState<
    Map<string, ColorPalette>
  >(new Map());
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<BoardTask | null>(null);
  const [isMemberCardView, setIsMemberCardView] = useState(false);
  const [draggedMember, setDraggedMember] =
    useState<GetCohortProgressMembersItem | null>(null);

  const canAddCards =
    user?.role === UserRole.ADMIN || user?.role === UserRole.MENTOR;

  // executeMemberCardMove: the board-specific move passed to useConfirmOnExit.
  // Only calls UPDATE_CARD_STATUS — optimistic local state is handled by the
  // onMoved callback passed to confirmOnExit.trigger() at drag time.
  const executeMemberCardMove = useCallback(
    async (taskId: string, statusId: string) => {
      await actionOrchestrator.execute<
        UpdateTaskStatusInput,
        UpdateTaskStatusOutput
      >(
        Action.UPDATE_TASK_STATUS,
        { task_id: taskId, status_id: statusId },
        { toast: false }
      );
    },
    []
  );

  const confirmOnExit = useConfirmOnExit(executeMemberCardMove);
  const confirmOnEntry = useConfirmOnEntry(executeMemberCardMove);

  const openCardPopup = useCallback(
    async (taskId: string, memberView: boolean) => {
      const result = await actionOrchestrator.execute<
        GetTaskInput,
        GetTaskOutput
      >(Action.GET_TASK, { task_id: taskId }, { toast: false });
      if (result.code === ResponseCode.SUCCESS && result.data?.card) {
        const c = result.data.card;
        setSelectedCard({
          id: c.id,
          board_id: c.board_id,
          organization_id: activeOrganizationId ?? '',
          status_id: c.status_id,
          title: c.title,
          description: c.description,
          metadata: c.metadata,
          created_by: c.created_by,
          created_at: c.created_at,
          priority: c.priority as BoardTask['priority'],
          estimated_pomodoros: c.estimated_pomodoros,
          actual_pomodoros: c.actual_pomodoros,
          start_date: c.start_date,
        });
        setIsMemberCardView(memberView);
        setIsEditPopupOpen(true);
      }
    },
    [activeOrganizationId]
  );

  const handleCardClick = useCallback(
    (taskId: string) => openCardPopup(taskId, false),
    [openCardPopup]
  );

  const handleBubbleClick = useCallback(
    (taskId: string) => openCardPopup(taskId, true),
    [openCardPopup]
  );

  const fetchProgress = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      setError(null);

      const result = await actionOrchestrator.execute<
        GetCohortProgressInput,
        GetCohortProgressOutput
      >(Action.GET_COHORT_PROGRESS, {
        board_id: boardId,
        week_number: selectedWeek.week,
        year: selectedWeek.year,
      });

      setIsLoading(false);

      if (result.code === ResponseCode.SUCCESS && result.data) {
        const sorted = [...result.data.statuses].sort(
          (a, b) => a.position - b.position
        );
        setStatuses(sorted);
        setTasks(result.data.tasks);

        // Fetch user profiles to get per-member theme colors
        const userIds = new Set<string>();
        for (const task of result.data.tasks) {
          for (const member of task.members) {
            userIds.add(member.user_id);
          }
        }

        const profileFetches = Array.from(userIds).map(user_id =>
          actionOrchestrator
            .execute<GetUserProfileInput, GetUserProfileOutput>(
              Action.GET_USER_PROFILE,
              { user_id },
              { toast: false }
            )
            .then(r => ({ user_id, data: r.data }))
            .catch(() => ({ user_id, data: null }))
        );

        const profiles = await Promise.all(profileFetches);
        const palMap = new Map<string, ColorPalette>();
        for (const { user_id, data } of profiles) {
          const palette = getPaletteForThemeString(data?.preferences?.theme);
          if (palette) palMap.set(user_id, palette);
        }
        setUserPaletteMap(palMap);
      } else {
        setError('Failed to load cohort progress');
      }
    },
    [boardId, selectedWeek.week, selectedWeek.year]
  );

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  const fetchProgressRef = useRef(fetchProgress);
  fetchProgressRef.current = fetchProgress;

  useEffect(() => {
    if (!refreshTrigger) return;
    fetchProgressRef.current(true);
  }, [refreshTrigger]);

  const handleMemberDragStart = useCallback(
    (member: GetCohortProgressMembersItem) => {
      setDraggedMember(member);
    },
    []
  );

  const handleMemberDragEnd = useCallback(() => {
    setDraggedMember(null);
  }, []);

  const handleMemberDrop = useCallback(
    async (targetStatusName: string) => {
      if (!draggedMember || draggedMember.status_name === targetStatusName) {
        setDraggedMember(null);
        return;
      }
      const member = draggedMember;
      setDraggedMember(null);

      // Resolve the target status_id on the member's personal board.
      // Prefer TemplateStatusID match (works across different board templates),
      // fall back to name match for boards sharing the same template.
      const cohortTargetStatus = statuses.find(
        s => s.name === targetStatusName
      );
      const targetStatus = cohortTargetStatus?.template_status_id
        ? (member.board_status_map.find(
            s => s.template_status_id === cohortTargetStatus.template_status_id
          ) ?? member.board_status_map.find(s => s.name === targetStatusName))
        : member.board_status_map.find(s => s.name === targetStatusName);
      if (!targetStatus) return;

      // Helper: move bubble in local state
      const applyMove = () =>
        setTasks(prev =>
          prev.map(task => ({
            ...task,
            members: task.members.map(m =>
              m.task_id === member.task_id
                ? {
                    ...m,
                    status_name: targetStatusName,
                    status_id: targetStatus.id,
                  }
                : m
            ),
          }))
        );

      // Helper: revert bubble in local state
      const revertMove = () =>
        setTasks(prev =>
          prev.map(task => ({
            ...task,
            members: task.members.map(m =>
              m.task_id === member.task_id
                ? {
                    ...m,
                    status_name: member.status_name,
                    status_id: member.status_id,
                  }
                : m
            ),
          }))
        );

      // Pre-flight validation using cohort board column configs (same template as member boards).
      // Capacity check passes cards:[] — backend enforces WIP limits authoritatively.
      if (statuses.length > 0 && user) {
        const sourceCohortStatus = statuses.find(
          s => s.name === member.status_name
        );
        const targetCohortStatus = statuses.find(
          s => s.name === targetStatusName
        );

        if (sourceCohortStatus && targetCohortStatus) {
          const toColumn = (
            s: (typeof statuses)[number]
          ): BoardStatusWithCards => ({
            id: s.id,
            board_id: boardId,
            name: s.name,
            position: s.position,
            color: s.color,
            config: s.config as Record<string, unknown> | undefined,
            cards: [],
          });

          const validation = validateCardMove(
            toColumn(sourceCohortStatus),
            toColumn(targetCohortStatus),
            user.role as UserRole
          );

          if (!validation.valid) {
            const fakeCard = { id: member.task_id } as unknown as BoardTask;

            if (
              validation.errorCode === 'CONFIRM_ON_EXIT_REQUIRED' &&
              validation.confirmOnExit
            ) {
              confirmOnExit.trigger(
                fakeCard,
                targetStatus.id,
                validation.confirmOnExit,
                applyMove
              );
              return;
            }

            if (
              validation.errorCode === 'CONFIRM_ON_ENTRY_REQUIRED' &&
              validation.confirmOnEntry
            ) {
              confirmOnEntry.trigger(
                fakeCard,
                targetStatus.id,
                validation.confirmOnEntry,
                applyMove
              );
              return;
            }

            alert.show({
              type: 'warning',
              title:
                validation.errorCode === 'CAPACITY_EXCEEDED'
                  ? 'Column Full'
                  : validation.errorCode === 'STUDENT_BACKWARD_MOVEMENT_BLOCKED'
                    ? 'Backward Movement Not Allowed'
                    : 'Cannot Move Card',
              message: validation.error || 'This move is not allowed',
              buttons: [{ label: 'Got it', onClick: () => {} }],
            });
            return;
          }
        }
      }

      // Optimistic update
      applyMove();

      const result = await actionOrchestrator.execute<
        UpdateTaskStatusInput,
        UpdateTaskStatusOutput
      >(
        Action.UPDATE_TASK_STATUS,
        { task_id: member.task_id, status_id: targetStatus.id },
        { toast: false }
      );

      const isSuccess =
        result.code === ResponseCode.SUCCESS ||
        result.code === ResponseCode.CREATED ||
        result.code === ResponseCode.ACCEPTED;

      if (isSuccess) return;

      // Roll back and handle server-side error
      revertMove();

      if (
        result.code === ResponseCode.CONFIRM_ON_EXIT_FIELD_REQUIRED &&
        result.data?.confirm_on_exit_field
      ) {
        const fakeCard = { id: member.task_id } as typeof member & {
          id: string;
        };
        confirmOnExit.trigger(
          fakeCard as unknown as BoardTask,
          targetStatus.id,
          { field: result.data.confirm_on_exit_field as ConfirmableField },
          applyMove
        );
      } else {
        alert.show({
          type: 'error',
          title: 'Cannot Move Card',
          message: getResponseCodeMessage(result.code),
          buttons: [{ label: 'Got it', onClick: () => {} }],
        });
      }
    },
    [
      draggedMember,
      statuses,
      user,
      boardId,
      confirmOnExit,
      confirmOnEntry,
      alert,
    ]
  );

  // Cast to the shape TaskEditPopup expects (cards: [] is fine for create mode)
  const statusesForPopup: BoardStatusWithCards[] = statuses.map(s => ({
    ...s,
    config: s.config as Record<string, unknown> | undefined,
    board_id: boardId,
    cards: [],
  }));

  if (isLoading) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={{ color: colors.text.secondary, fontSize: typography.sizes.sm }}
      >
        Loading progress...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        style={{ color: colors.error, fontSize: typography.sizes.sm }}
      >
        {error}
      </div>
    );
  }

  if (tasks.length === 0 && !canAddCards) {
    return (
      <DefaultEmptyState
        config={{
          title: 'Nothing here for this week',
          description:
            "Your mentor hasn't synced any assignments for this week yet. Check back later, or reach out if you think something's missing.",
          icon: (
            <Icon
              name={IconName.CALENDAR}
              size="xl"
              color={colors.text.tertiary}
            />
          ),
        }}
      />
    );
  }

  return (
    <div
      className="flex-1 overflow-auto relative"
      style={{ padding: spacing.md }}
    >
      {tasks.length === 0 ? (
        <DefaultEmptyState
          config={{
            title: 'No cards on this board yet',
            description:
              'Add cards here, then sync them so members see their assignments for the week.',
            icon: (
              <Icon
                name={IconName.KANBAN}
                size="xl"
                color={colors.text.tertiary}
              />
            ),
            actionText: 'Create Card',
            onAction: () => setIsCreatePopupOpen(true),
          }}
        />
      ) : (
        <>
          <ProgressMatrix
            statuses={statuses}
            tasks={tasks}
            userPaletteMap={userPaletteMap}
            userMap={userMap}
            onCardClick={handleCardClick}
            onBubbleClick={handleBubbleClick}
            canDragMembers={canAddCards}
            draggedMember={draggedMember}
            onMemberDragStart={handleMemberDragStart}
            onMemberDragEnd={handleMemberDragEnd}
            onMemberDrop={handleMemberDrop}
          />

          {/* FAB — same pattern as KanbanBoard */}
          {canAddCards && (
            <button
              onClick={() => setIsCreatePopupOpen(true)}
              className="fixed rounded-full shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50"
              style={{
                bottom: `max(${spacing.md}, env(safe-area-inset-bottom, 0px))`,
                right: spacing.xl,
                width: sizing.button.xl,
                height: sizing.button.xl,
                backgroundColor: colors.blue,
                color: colors.text.inverse,
              }}
              title="Create new card"
            >
              <Icon
                name={IconName.PLUS}
                size="xl"
                color={colors.text.inverse}
              />
            </button>
          )}
        </>
      )}

      {/* Create popup */}
      {canAddCards && activeOrganizationId && (
        <TaskEditPopup
          isOpen={isCreatePopupOpen}
          onClose={() => setIsCreatePopupOpen(false)}
          card={null}
          statuses={statusesForPopup}
          onCardUpdated={() => {
            setIsCreatePopupOpen(false);
            fetchProgress();
          }}
          organizationId={activeOrganizationId}
          boardId={boardId}
          isCreateMode
        />
      )}

      {/* Edit popup — opened on card click or bubble click */}
      {activeOrganizationId && (
        <TaskEditPopup
          isOpen={isEditPopupOpen}
          onClose={() => {
            setIsEditPopupOpen(false);
            setSelectedCard(null);
            setIsMemberCardView(false);
          }}
          card={selectedCard}
          statuses={statusesForPopup}
          onCardUpdated={() => {
            setIsEditPopupOpen(false);
            setSelectedCard(null);
            setIsMemberCardView(false);
            fetchProgress();
          }}
          organizationId={activeOrganizationId}
          boardId={isMemberCardView ? selectedCard?.board_id : boardId}
          isAdmin={canAddCards}
          isFrozen={isMemberCardView && !canAddCards}
          isCommentAllowed={canAddCards && isMemberCardView}
          boardTag={isMemberCardView ? undefined : BoardTag.COHORT}
          onNavigateToCard={navCard => {
            setSelectedCard(navCard);
            setIsMemberCardView(false);
          }}
        />
      )}

      {/* Confirm-on-exit dialog — same as KanbanBoard, driven by useConfirmOnExit */}
      {confirmOnExit.state && (
        <ConfirmOnExitDialog
          open={!!confirmOnExit.state}
          onOpenChange={open => {
            if (!open) confirmOnExit.dismiss();
          }}
          card={confirmOnExit.state.card}
          fieldConfig={confirmOnExit.state.fieldConfig}
          onConfirm={confirmOnExit.handleSubmit}
          isSubmitting={confirmOnExit.isSubmitting}
        />
      )}

      {/* Confirm-on-entry dialog — same as KanbanBoard, driven by useConfirmOnEntry */}
      {confirmOnEntry.state && (
        <ConfirmOnEntryDialog
          open={!!confirmOnEntry.state}
          onOpenChange={open => {
            if (!open) confirmOnEntry.dismiss();
          }}
          card={confirmOnEntry.state.card}
          fieldConfig={confirmOnEntry.state.fieldConfig}
          onConfirm={confirmOnEntry.handleSubmit}
          isSubmitting={confirmOnEntry.isSubmitting}
        />
      )}

      {/* Alerts: dialog field-update errors + other move errors */}
      <Alert {...confirmOnExit.alertProps} />
      <Alert {...confirmOnEntry.alertProps} />
      <Alert {...alert.props} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProgressMatrix — row-aligned grid (header row + one row per task)
// ---------------------------------------------------------------------------

const STATUS_COL_WIDTH = layout.kanban.columnWidth;

function ProgressMatrix({
  statuses,
  tasks,
  userPaletteMap,
  userMap,
  onCardClick,
  onBubbleClick,
  canDragMembers,
  draggedMember,
  onMemberDragStart,
  onMemberDragEnd,
  onMemberDrop,
}: {
  statuses: GetCohortProgressStatusesItem[];
  tasks: GetCohortProgressTasksItem[];
  userPaletteMap: Map<string, ColorPalette>;
  userMap: Parameters<typeof getUserDisplayName>[0];
  onCardClick: (taskId: string) => void;
  onBubbleClick: (taskId: string) => void;
  canDragMembers: boolean;
  draggedMember: GetCohortProgressMembersItem | null;
  onMemberDragStart: (member: GetCohortProgressMembersItem) => void;
  onMemberDragEnd: () => void;
  onMemberDrop: (targetStatusName: string) => void;
}) {
  return (
    <div className="inline-block">
      {/* ── Header row — status columns only (card column has no label) ── */}
      <div
        className="flex items-stretch sticky top-0 z-10"
        style={{ gap: layout.kanban.columnGap }}
      >
        {/* Spacer matching card column width */}
        <div
          className="flex-shrink-0"
          style={{ width: STATUS_COL_WIDTH, minWidth: STATUS_COL_WIDTH }}
        />

        {/* Status column headers — same style as KanbanColumn header */}
        {statuses.map(status => (
          <StatusHeader key={status.id} status={status} />
        ))}
      </div>

      {/* ── Task rows ────────────────────────────────────────── */}
      <div
        className="flex flex-col"
        style={{
          gap: layout.kanban.columnGap,
          paddingTop: layout.kanban.columnGap,
        }}
      >
        {tasks.map(task => {
          const creatorName = getUserDisplayName(userMap, task.created_by);
          return (
            <TaskRow
              key={task.task_id}
              task={task}
              statuses={statuses}
              userPaletteMap={userPaletteMap}
              creatorName={creatorName}
              onCardClick={onCardClick}
              onBubbleClick={onBubbleClick}
              canDragMembers={canDragMembers}
              draggedMember={draggedMember}
              onMemberDragStart={onMemberDragStart}
              onMemberDragEnd={onMemberDragEnd}
              onMemberDrop={onMemberDrop}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusHeader — styled to match KanbanColumn header
// ---------------------------------------------------------------------------

function StatusHeader({ status }: { status: GetCohortProgressStatusesItem }) {
  const colors = useThemeColors();
  const dotColor = status.color ?? colors.text.tertiary;

  return (
    <div
      className="flex items-center gap-2 rounded-xl flex-shrink-0"
      style={{
        width: STATUS_COL_WIDTH,
        minWidth: STATUS_COL_WIDTH,
        padding: `${spacing.sm} ${spacing.md}`,
        backgroundColor: colors.offWhite,
      }}
    >
      <div
        className="w-3 h-3 rounded-full flex-shrink-0"
        style={{ backgroundColor: dotColor }}
      />
      <h3
        className="font-semibold truncate"
        style={{ color: colors.text.primary }}
        title={status.name}
      >
        {status.name}
      </h3>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TaskRow — one row per cohort card
// ---------------------------------------------------------------------------

function TaskRow({
  task,
  statuses,
  userPaletteMap,
  creatorName,
  onCardClick,
  onBubbleClick,
  canDragMembers,
  draggedMember,
  onMemberDragStart,
  onMemberDragEnd,
  onMemberDrop,
}: {
  task: GetCohortProgressTasksItem;
  statuses: GetCohortProgressStatusesItem[];
  userPaletteMap: Map<string, ColorPalette>;
  creatorName: string;
  onCardClick: (taskId: string) => void;
  onBubbleClick: (taskId: string) => void;
  canDragMembers: boolean;
  draggedMember: GetCohortProgressMembersItem | null;
  onMemberDragStart: (member: GetCohortProgressMembersItem) => void;
  onMemberDragEnd: () => void;
  onMemberDrop: (targetStatusName: string) => void;
}) {
  const colors = useThemeColors();

  const displayCard: BoardTask = {
    id: task.task_id,
    board_id: '',
    organization_id: '',
    status_id: '',
    title: task.title,
    created_by: task.created_by,
  };

  return (
    <div
      className="flex items-stretch"
      style={{ gap: layout.kanban.columnGap }}
    >
      {/* Card column — same width as status columns, p-4 to match KanbanColumn */}
      <div
        className="flex-shrink-0 rounded-xl shadow-soft p-4"
        style={{
          width: STATUS_COL_WIDTH,
          minWidth: STATUS_COL_WIDTH,
          backgroundColor: colors.offWhite,
        }}
      >
        <KanbanCard
          card={displayCard}
          creatorName={creatorName}
          onClick={() => onCardClick(task.task_id)}
        />
      </div>

      {/* Status cells */}
      {statuses.map(status => {
        const membersInStatus = task.members.filter(
          s => s.status_name === status.name
        );
        return (
          <StatusCell
            key={status.id}
            statusName={status.name}
            members={membersInStatus}
            statusColor={status.color}
            userPaletteMap={userPaletteMap}
            onBubbleClick={onBubbleClick}
            canDragMembers={canDragMembers}
            isDragActive={draggedMember !== null}
            onMemberDragStart={onMemberDragStart}
            onMemberDragEnd={onMemberDragEnd}
            onDrop={onMemberDrop}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusCell — user bubbles for a single card × status intersection
// ---------------------------------------------------------------------------

function StatusCell({
  statusName,
  members,
  statusColor,
  userPaletteMap,
  onBubbleClick,
  canDragMembers,
  isDragActive,
  onMemberDragStart,
  onMemberDragEnd,
  onDrop,
}: {
  statusName: string;
  members: GetCohortProgressMembersItem[];
  statusColor?: string;
  userPaletteMap: Map<string, ColorPalette>;
  onBubbleClick: (taskId: string) => void;
  canDragMembers: boolean;
  isDragActive: boolean;
  onMemberDragStart: (member: GetCohortProgressMembersItem) => void;
  onMemberDragEnd: () => void;
  onDrop: (targetStatusName: string) => void;
}) {
  const colors = useThemeColors();
  const [isDragOver, setIsDragOver] = useState(false);

  const borderColor = isDragOver
    ? (statusColor ?? colors.blue)
    : statusColor
      ? withOpacity(statusColor, opacity.subtle)
      : colors.border.default;

  return (
    <div
      className="flex-shrink-0 flex flex-wrap items-start content-start shadow-soft rounded-xl"
      style={{
        width: STATUS_COL_WIDTH,
        minWidth: STATUS_COL_WIDTH,
        padding: spacing.sm,
        gap: spacing.xs,
        backgroundColor: isDragOver
          ? withOpacity(statusColor ?? colors.blue, opacity.subtle)
          : colors.offWhite,
        border: `1px solid ${borderColor}`,
        minHeight: layout.kanban.cardHeight,
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
      onDragOver={e => {
        if (!isDragActive) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={e => {
        e.preventDefault();
        setIsDragOver(false);
        onDrop(statusName);
      }}
    >
      {members.map(member => (
        <UserBubble
          key={member.user_id}
          member={member}
          palette={userPaletteMap.get(member.user_id) ?? null}
          onClick={() => onBubbleClick(member.task_id)}
          canDrag={canDragMembers}
          onDragStart={onMemberDragStart}
          onDragEnd={onMemberDragEnd}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserBubble — avatar + name styled with the student's own theme
// ---------------------------------------------------------------------------

function UserBubble({
  member,
  palette,
  onClick,
  canDrag,
  onDragStart,
  onDragEnd,
}: {
  member: GetCohortProgressMembersItem;
  palette: ColorPalette | null;
  onClick: () => void;
  canDrag: boolean;
  onDragStart: (member: GetCohortProgressMembersItem) => void;
  onDragEnd: () => void;
}) {
  const fallback = useThemeColors();
  const c = palette ?? fallback;

  return (
    <button
      type="button"
      draggable={canDrag}
      onClick={onClick}
      onDragStart={
        canDrag
          ? e => {
              e.dataTransfer.effectAllowed = 'move';
              onDragStart(member);
            }
          : undefined
      }
      onDragEnd={canDrag ? onDragEnd : undefined}
      className="flex items-center gap-1 flex-shrink-0 transition-opacity hover:opacity-75"
      style={{
        backgroundColor: c.background,
        border: `1.5px solid ${c.primary}`,
        borderRadius: radius.full,
        padding: `2px ${spacing.xs} 2px 2px`,
        cursor: canDrag ? 'grab' : 'pointer',
      }}
      title={
        canDrag
          ? `Drag to move ${member.display_name}'s card`
          : `View ${member.display_name}'s card`
      }
    >
      <Avatar
        src={member.profile_image}
        displayName={member.display_name}
        userId={member.user_id}
        size={22}
      />
      <span
        style={{
          fontSize: typography.sizes.xs,
          color: c.text.primary,
          fontWeight: 500,
          maxWidth: 80,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {member.display_name}
      </span>
    </button>
  );
}
